// =========================================================
// NORTH — Header Premium
// Header, contador do carrinho e busca mobile em tempo real
// =========================================================

(() => {
  const HEADER_SELECTOR = '#header-component';

  const BUBBLE_SELECTORS = [
    '.cart-bubble',
    '[class*="cart-bubble"]',
    '[data-testid*="cart-bubble"]'
  ];

  let header = null;
  let lastItemCount = null;
  let cartObserver = null;

  // =========================================================
  // CARRINHO
  // =========================================================

  const getCartBubble = () => {
    return header?.querySelector(BUBBLE_SELECTORS.join(',')) || null;
  };

  const readBubbleCount = (bubble) => {
    if (!bubble) return null;

    const raw = bubble.textContent?.trim() || '';
    const parsed = Number.parseInt(raw.replace(/\D/g, ''), 10);

    return Number.isFinite(parsed) ? parsed : null;
  };

  const animateCartBubble = () => {
    const bubble = getCartBubble();

    if (!bubble) return;

    bubble.classList.remove('north-cart-bubble-pop');

    void bubble.offsetWidth;

    bubble.classList.add('north-cart-bubble-pop');

    window.setTimeout(() => {
      bubble.classList.remove('north-cart-bubble-pop');
    }, 500);
  };

  const observeCartBubble = () => {
    if (!header) return;

    cartObserver?.disconnect();

    cartObserver = new MutationObserver(() => {
      const bubble = getCartBubble();
      const nextCount = readBubbleCount(bubble);

      if (
        nextCount !== null &&
        lastItemCount !== null &&
        nextCount !== lastItemCount
      ) {
        animateCartBubble();
      }

      if (nextCount !== null) {
        lastItemCount = nextCount;
      }
    });

    cartObserver.observe(header, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true
    });

    lastItemCount = readBubbleCount(getCartBubble());
  };

  const bindCartEvents = () => {
    const events = [
      'cart:update',
      'cart:refresh',
      'north:cart-updated'
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, () => {
        window.requestAnimationFrame(() => {
          animateCartBubble();
          lastItemCount = readBubbleCount(getCartBubble());
        });
      });
    });
  };

  // =========================================================
  // HEADER AO ROLAR
  // =========================================================

  const updateScrollState = () => {
    if (!header) return;

    header.classList.toggle(
      'north-header--scrolled',
      window.scrollY > 10
    );
  };

  // =========================================================
  // BUSCA MOBILE EM TEMPO REAL
  // =========================================================

  const escapeHtml = (value = '') => {
    const div = document.createElement('div');

    div.textContent = String(value);

    return div.innerHTML;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const normalizedValue = String(value)
      .replace('R$', '')
      .replace(/\s/g, '')
      .replace(',', '.');

    const parsedValue = Number.parseFloat(normalizedValue);

    if (!Number.isFinite(parsedValue)) {
      return String(value);
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(parsedValue);
  };

  const getProductImage = (product) => {
    return (
      product?.featured_image?.url ||
      product?.featured_image ||
      product?.image ||
      product?.images?.[0] ||
      ''
    );
  };

  const renderSearchStatus = (resultsElement, message) => {
    resultsElement.hidden = false;

    resultsElement.innerHTML = `
      <p class="north-drawer-search__status">
        ${escapeHtml(message)}
      </p>
    `;
  };

  const renderSearchResults = (
    resultsElement,
    products,
    searchTerm
  ) => {
    if (!products.length) {
      resultsElement.hidden = false;

      resultsElement.innerHTML = `
        <p class="north-drawer-search__empty">
          Nenhum produto encontrado para “${escapeHtml(searchTerm)}”.
        </p>
      `;

      return;
    }

    const root =
      window.Shopify?.routes?.root ||
      '/';

    const productsHtml = products
      .map((product) => {
        const imageUrl = getProductImage(product);
        const productUrl = product.url || '#';
        const title = product.title || 'Produto NORTH';
        const price = formatPrice(
          product.price ||
          product.price_min
        );

        const imageHtml = imageUrl
          ? `
            <img
              src="${escapeHtml(imageUrl)}"
              alt="${escapeHtml(title)}"
              loading="lazy"
              width="116"
              height="132"
            >
          `
          : '';

        return `
          <a
            class="north-drawer-search__item"
            href="${escapeHtml(productUrl)}"
          >
            <span class="north-drawer-search__image">
              ${imageHtml}
            </span>

            <span class="north-drawer-search__copy">
              <span class="north-drawer-search__title">
                ${escapeHtml(title)}
              </span>

              ${
                price
                  ? `
                    <span class="north-drawer-search__price">
                      ${escapeHtml(price)}
                    </span>
                  `
                  : ''
              }
            </span>

            <span
              class="north-mobile-menu__arrow"
              aria-hidden="true"
            ></span>
          </a>
        `;
      })
      .join('');

    const fullSearchUrl =
      `${root}search?q=${encodeURIComponent(searchTerm)}&type=product`;

    resultsElement.hidden = false;

    resultsElement.innerHTML = `
      ${productsHtml}

      <a
        class="north-drawer-search__all"
        href="${fullSearchUrl}"
      >
        <span>Ver todos os resultados</span>

        <span
          class="north-mobile-menu__arrow"
          aria-hidden="true"
        ></span>
      </a>
    `;
  };

  const initDrawerSearch = () => {
    const searchForms = document.querySelectorAll(
      '[data-north-drawer-search-form]'
    );

    searchForms.forEach((form) => {
      if (form.dataset.northSearchReady === 'true') {
        return;
      }

      form.dataset.northSearchReady = 'true';

      const input = form.querySelector(
        '[data-north-drawer-search-input]'
      );

      const resultsElement = form.parentElement?.querySelector(
        '[data-north-drawer-search-results]'
      );

      if (!input || !resultsElement) {
        return;
      }

      let debounceTimer = null;
      let requestController = null;

      const closeResults = () => {
        resultsElement.hidden = true;
        resultsElement.innerHTML = '';

        input.setAttribute('aria-expanded', 'false');
      };

      const executeSearch = async () => {
        const searchTerm = input.value.trim();

        if (searchTerm.length < 2) {
          closeResults();
          return;
        }

        requestController?.abort();

        requestController = new AbortController();

        renderSearchStatus(
          resultsElement,
          'Buscando produtos...'
        );

        input.setAttribute('aria-expanded', 'true');

        try {
          const root =
            window.Shopify?.routes?.root ||
            '/';

          const params = new URLSearchParams({
            q: searchTerm,
            'resources[type]': 'product',
            'resources[limit]': '5',
            'resources[options][unavailable_products]': 'last'
          });

          const response = await fetch(
            `${root}search/suggest.json?${params.toString()}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json'
              },
              signal: requestController.signal
            }
          );

          if (!response.ok) {
            throw new Error(
              `Erro na busca: ${response.status}`
            );
          }

          const data = await response.json();

          const products =
            data?.resources?.results?.products ||
            [];

          renderSearchResults(
            resultsElement,
            products,
            searchTerm
          );
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }

          console.error(
            'NORTH — erro na busca mobile:',
            error
          );

          renderSearchStatus(
            resultsElement,
            'Não foi possível carregar a busca agora.'
          );
        }
      };

      input.addEventListener('input', () => {
        window.clearTimeout(debounceTimer);

        const searchTerm = input.value.trim();

        if (searchTerm.length < 2) {
          closeResults();
          return;
        }

        debounceTimer = window.setTimeout(
          executeSearch,
          280
        );
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2) {
          executeSearch();
        }
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeResults();
          input.blur();
        }
      });

      form.addEventListener('submit', (event) => {
        const searchTerm = input.value.trim();

        if (!searchTerm) {
          event.preventDefault();
          input.focus();
        }
      });

      document.addEventListener('click', (event) => {
        if (!form.parentElement?.contains(event.target)) {
          closeResults();
        }
      });
    });
  };

  // =========================================================
  // INICIALIZAÇÃO
  // =========================================================

  const initHeader = () => {
    header = document.querySelector(HEADER_SELECTOR);

    if (!header) return;

    if (header.dataset.northHeaderReady !== 'true') {
      header.dataset.northHeaderReady = 'true';

      updateScrollState();
      observeCartBubble();
      bindCartEvents();

      window.addEventListener(
        'scroll',
        updateScrollState,
        { passive: true }
      );
    }

    initDrawerSearch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initHeader,
      { once: true }
    );
  } else {
    initHeader();
  }

  document.addEventListener(
    'shopify:section:load',
    initHeader
  );
})();